/**
 * Hook for managing real-time message subscriptions
 * Handles Wix API subscriptions and live message updates
 */

import { useEffect, useRef } from 'react';
import { InboxService } from '@rsmaids/shared/services/api/inboxService';
import { Message as SharedMessage } from '@rsmaids/shared/types/Inbox';
import { parseWixMessage } from '../services/messageParser';
import { applyStaffLookup } from '../services/messageEnrichment';
import { StaffCacheService } from '@rsmaids/shared/services/api/staffCacheService';

interface Message {
  id?: string;
  from: string;
  text: string;
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email' | 'call';
  _createdDate: Date;
  isFromCustomer?: boolean;
  isSystemMessage?: boolean;
  avatarUrl?: string;
  extractedStaffInfo?: { staffName: string; avatarUrl?: string };
  invoicePaidData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; icon: string };
  newOrderData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; imageUrl?: string };
  media?: Array<{
    url?: string;
    type: 'image' | 'video' | 'file' | 'audio' | 'icon';
    name?: string;
    iconName?: string;
    duration?: number;
  }>;
  senderStaffName?: string;
  senderStaffAvatar?: string;
  senderType?: string;
  senderStaffId?: string;
  targetChannelIds?: string[]; // Wix channel IDs for outbound routing
  _callData?: {
    callId: string;
    direction: 'inbound' | 'outbound';
    duration: number;
    recordingUrl?: string;
    callerName?: string;
    customerNumber: string;
  };
  _smsData?: {
    direction: 'inbound' | 'outbound';
    customerNumber: string;
  };
}

export interface UseMessageSubscriptionProps {
  realId: string;
  initialLoadDone: boolean;
  contactName: string;
  contactAvatarUrl?: string;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  seenMessageIdsRef: React.MutableRefObject<Set<string>>;
}

export const useMessageSubscription = ({
  realId,
  initialLoadDone,
  contactName,
  contactAvatarUrl,
  setMessages,
  seenMessageIdsRef,
}: UseMessageSubscriptionProps) => {
  const unsubscribeRef = useRef<(() => void) | undefined>(undefined);

  // The name/avatar are only used to label incoming messages. Read them through a ref so a change
  // doesn't tear down and re-create the subscription (each re-subscribe re-delivers every message).
  const labelsRef = useRef({ contactName, contactAvatarUrl });
  useEffect(() => {
    labelsRef.current = { contactName, contactAvatarUrl };
  });

  useEffect(() => {
    console.log(`🔍 [useMessageSubscription] EFFECT RUNNING: realId=${realId}, initialLoadDone=${initialLoadDone}`);
    
    if (!realId) {
      console.log(`📭 [useMessageSubscription] Skipping subscription - no realId`);
      return;
    }

    if (!initialLoadDone) {
      console.log(`📭 [useMessageSubscription] Skipping subscription - initialLoadDone=${initialLoadDone}`);
      return;
    }

    // Skip subscription for temp IDs
    if (realId.startsWith('temp-')) {
      console.log(`📭 [useMessageSubscription] Skipping subscription for temp ID ${realId}`);
      return;
    }

    let mounted = true;

    console.log(`🎯 [useMessageSubscription] ✅ READY TO SUBSCRIBE! Setting up real-time subscription for conversation ${realId}...`);

    InboxService.subscribeToMessages(realId, (_updatedMessages: SharedMessage[]) => {
      if (!mounted) {
        console.log(`⏳ [useMessageSubscription] Real-time: Blocking - initial load or unmount`);
        return;
      }

      console.log(`📨 [useMessageSubscription] Real-time: Received ${_updatedMessages.length} messages from Firestore`);
      console.log(`📨 [useMessageSubscription] First message IDs:`, _updatedMessages.slice(0, 3).map(m => ({ id: m.id, text: String(m.text || '').substring(0, 30) })));
      if (!_updatedMessages || _updatedMessages.length === 0) {
        return;
      }

      const applyUpdates = async () => {
        // SMS messages are pre-formatted — skip Wix parsing/enrichment entirely
        if (realId.startsWith('sms-')) {
          const smsMessages = [...(_updatedMessages as unknown as Message[])].reverse() as Message[];
          const latestId = smsMessages[smsMessages.length - 1]?.id;
          setMessages((prev) => {
            const prevLatestId = prev[prev.length - 1]?.id;
            if (prev.length === smsMessages.length && prevLatestId === latestId) return prev;
            return smsMessages;
          });
          seenMessageIdsRef.current = new Set(
            smsMessages.map(m => m.id).filter((id): id is string => typeof id === 'string')
          );
          return;
        }

        const uiMessages: Message[] = (_updatedMessages as unknown[]).map((msg) =>
          parseWixMessage(msg, labelsRef.current.contactName || 'Contact') as unknown as Message
        );

        // Firestore's first snapshot repeats the messages the initial load just showed, and a
        // re-subscribe repeats them again. When every message is one we already have there is
        // nothing to update — skip the enrichment and the state change entirely.
        if (uiMessages.every(msg => msg.id && seenMessageIdsRef.current.has(msg.id))) {
          return;
        }

        const staffCache = StaffCacheService.getInstance();
        if (!staffCache.isStaffCacheLoaded()) {
          await staffCache.loadStaffCache();
        }

        const enrichedMessages = uiMessages.map((msg) => {
          const hasCompleteEnrichment = msg.senderStaffName && msg.avatarUrl;
          if (hasCompleteEnrichment) {
            return msg;
          }
          const enrichedMsg = applyStaffLookup(msg, staffCache);
          // For customer messages, inject contact avatar
          const avatarForCustomer = labelsRef.current.contactAvatarUrl;
          if (enrichedMsg.isFromCustomer && avatarForCustomer && !enrichedMsg.avatarUrl) {
            enrichedMsg.avatarUrl = avatarForCustomer;
          }
          return enrichedMsg;
        });

        // Merge, don't replace: add only the messages that are new. Replacing the list with the
        // snapshot would throw away older messages already loaded by scrolling up, and re-render
        // every message on screen for one new arrival.
        const incoming = (enrichedMessages as unknown as Message[]).filter(
          (msg) => !msg.id || !seenMessageIdsRef.current.has(msg.id)
        );
        incoming.forEach((msg) => { if (msg.id) seenMessageIdsRef.current.add(msg.id); });

        setMessages((prev) => {
          const have = new Set(prev.map((m) => m.id));
          const additions = incoming.filter((m) => !m.id || !have.has(m.id));
          if (additions.length === 0) return prev;
          return [...prev, ...additions].sort(
            (x, y) => new Date(x._createdDate).getTime() - new Date(y._createdDate).getTime()
          );
        });
      };

      void applyUpdates();
    })
      .then((unsub: () => void) => {
        if (mounted) {
          unsubscribeRef.current = unsub;
          console.log(`✅ [useMessageSubscription] Real-time subscription established`);
        }
      })
      .catch((err: Error) => {
        console.error('[useMessageSubscription] Failed to subscribe to real-time:', err);
      });

    return () => {
      mounted = false;
      if (unsubscribeRef.current) {
        console.log(`📭 [useMessageSubscription] Unsubscribing from message updates for conversation ${realId}`);
        unsubscribeRef.current();
      }
    };
  }, [realId, initialLoadDone, setMessages, seenMessageIdsRef]);

  return {
    unsubscribeRef,
  };
};
